import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const rupiah = (v) => "Rp" + Math.round(v || 0).toLocaleString("id-ID");

export default function ShiftsPage() {
  const { user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(user?.outlet_id || "");
  const [active, setActive] = useState(null);
  const [opening, setOpening] = useState("");
  const [closing, setClosing] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shifts/active${outletId ? `?outlet_id=${outletId}` : ""}`);
      setActive(data || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat shift"); }
  }, [outletId]);
  useEffect(() => {
    let alive = true;
    api.get("/outlets").then(({ data }) => {
      if (alive) {
        setOutlets(data);
        if (!user?.outlet_id && data[0]) setOutletId(current => current || data[0].id);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.outlet_id]);
  useEffect(() => { if (outletId) load(); }, [load, outletId]);

  async function open() {
    if (!outletId || Number(opening) < 0) return toast.error("Outlet dan modal awal wajib valid");
    setLoading(true);
    try { const {data} = await api.post("/shifts/open", { outlet_id: outletId, opening_cash: Number(opening) }); setActive(data); toast.success("Shift dibuka"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka shift"); }
    finally { setLoading(false); }
  }
  async function close() {
    if (!active || Number(closing) < 0) return toast.error("Kas akhir wajib valid");
    setLoading(true);
    try { const {data} = await api.post("/shifts/close", { shift_id: active.id, closing_cash: Number(closing), notes }); setActive(data); toast.success(`Shift ditutup · variance ${rupiah(data.cash_variance)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menutup shift"); }
    finally { setLoading(false); }
  }

  return <div className="page-shell max-w-4xl">
    <div data-workspace-redundant-header><div><h1 className="text-2xl font-extrabold font-display">Shift Kasir</h1></div><p className="text-slate-500 text-sm">Buka, pantau, dan tutup shift secara transactional.</p></div>
    <Card className="p-5 mt-4 space-y-4">
      <Select value={outletId} onValueChange={setOutletId}><SelectTrigger><SelectValue placeholder="Pilih outlet"/></SelectTrigger><SelectContent>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}</SelectContent></Select>
      {!active || active.status !== "OPEN" ? <>
        <div className="text-sm font-semibold">Modal Awal</div><Input type="number" min="0" value={opening} onChange={e=>setOpening(e.target.value)} placeholder="0"/>
        <Button disabled={loading} onClick={open} className="bg-orange-500 hover:bg-orange-600">Buka Shift</Button>
      </> : <>
        <div className="flex items-center justify-between"><div><div className="font-semibold">Shift aktif</div><div className="text-xs text-slate-500">{active.cashier_name} · {active.opened_at}</div></div><Badge className="bg-emerald-500">OPEN</Badge></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div className="p-3 rounded bg-slate-50"><div className="text-xs text-slate-500">Modal</div><div className="font-bold">{rupiah(active.opening_cash)}</div></div><div className="p-3 rounded bg-slate-50"><div className="text-xs text-slate-500">Penjualan Cash</div><div className="font-bold">{rupiah(active.cash_sales)}</div></div><div className="p-3 rounded bg-slate-50"><div className="text-xs text-slate-500">Kas Ekspektasi</div><div className="font-bold">{rupiah(active.expected_cash)}</div></div></div>
        <div className="text-sm font-semibold">Kas Akhir Fisik</div><Input type="number" min="0" value={closing} onChange={e=>setClosing(e.target.value)} placeholder="0"/>
        <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Catatan penutupan"/>
        <Button disabled={loading} onClick={close} className="bg-slate-900">Tutup Shift</Button>
      </>}
      {active?.status === "CLOSED" && <div className="p-4 rounded-lg bg-slate-50"><div className="font-semibold">Shift Closed</div><div className="text-sm mt-1">Expected: {rupiah(active.expected_cash)} · Actual: {rupiah(active.closing_cash)} · Variance: <b>{rupiah(active.cash_variance)}</b></div></div>}
    </Card>
  </div>;
}
